import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from "@reduxjs/toolkit";
import { client } from "../../api/client";
import { extractUserClaims } from "../../utils/jwtUtils";
// import { setAttendanceTracking } from "./attendance";

let auth = JSON.parse(localStorage.getItem("auth"));
const authenticationAdapter = createEntityAdapter();
if (auth) {
  client.setAuthorizationToken(auth["access_token"]);
}

/**
 * Build user object with role claims extracted from the signed JWT
 * This ensures role info cannot be tampered with via response interception
 * @param {object} authPayload - The authentication response from server
 * @returns {object} - User object with verified role claims
 */
function buildSecureUser(authPayload) {
  const user = authPayload.user || {};
  const token = authPayload.access_token;
  
  // Extract role claims from the signed JWT (tamper-proof)
  const jwtClaims = extractUserClaims(token);
  
  return {
    id: user.id,
    user_name: user.user_name,
    email: user.email,
    // Use JWT claims for role info - these are cryptographically signed
    is_admin: jwtClaims.is_admin,
    is_admin_or_qa: jwtClaims.is_admin_or_qa,
  };
}

// On app load, rebuild user from JWT claims to ensure localStorage wasn't tampered with
const initialUser = auth ? buildSecureUser(auth) : {};

const initialState = authenticationAdapter.getInitialState({
  status: "idle",
  isAuthenticated: !!auth,
  user: initialUser,
  pendingChallenge: null,
  twoFactorMessage: "",
  twoFactorEmailHint: "",
});

export const login = createAsyncThunk("authentication/login", async (data) => {
  return await client.post("/login", data);
});

export const verifyOtp = createAsyncThunk("authentication/verifyOtp", async (data) => {
  return await client.post("/login/verify-otp", data);
});

export const googleSsoLogin = createAsyncThunk("authentication/googleSsoLogin", async (_, { rejectWithValue }) => {
  try {
    const response = await client.post("/auth/google", {});
    return response;
  } catch (error) {
    return rejectWithValue(error.response?.data || { message: 'SSO login failed' });
  }
});

export const handleGoogleCallback = createAsyncThunk("authentication/handleGoogleCallback", async (authData) => {
  return authData;
});

const authenticationSlice = createSlice({
  name: "authentication",
  initialState: initialState,
  reducers: {
    logout(state, action) {
      const selectedStatus = localStorage.getItem("selectedStatus");
      const auth = JSON.parse(localStorage.getItem("auth") || "{}");
      if (selectedStatus && auth?.user?.id) {
        client.post("/setAttendance", {
          type: selectedStatus,
          label: "stop",
          userId: auth.user.id,
        });
      }
      localStorage.removeItem("selectedStatus");
      localStorage.removeItem("breakLogsStartTime");
      // setAttendanceTracking(client.post);

      client.logout();
      state.isAuthenticated = false;
      state.user = {};
      state.pendingChallenge = null;
      state.twoFactorMessage = "";
      state.twoFactorEmailHint = "";
    },
    clearTwoFactorChallenge(state) {
      state.pendingChallenge = null;
      state.twoFactorMessage = "";
      state.twoFactorEmailHint = "";
    },
  },
  extraReducers: {
    [login.pending]: (state, action) => {
      state.status = "pending";
    },
    [login.fulfilled]: (state, action) => {
      state.status = "idle";
      if (action.payload?.status === "otp_required") {
        state.isAuthenticated = false;
        state.pendingChallenge = action.payload.challenge_token;
        state.twoFactorMessage = action.payload.message;
        state.twoFactorEmailHint = action.payload.email_hint;
        return;
      }
      state.isAuthenticated = true;
      const auth = action.payload;
      // Build user with role claims from signed JWT (tamper-proof)
      const secureUser = buildSecureUser(auth);
      const secureAuth = { ...auth, user: secureUser };
      localStorage.setItem("auth", JSON.stringify(secureAuth));
      state.user = secureUser;
      client.setAuthorizationToken(auth["access_token"]);
    },
    [login.rejected]: (state, action) => {
      state.status = "idle";
      state.isAuthenticated = false;
    },
    [googleSsoLogin.pending]: (state, action) => {
      state.status = "pending";
    },
    [googleSsoLogin.fulfilled]: (state, action) => {
      state.status = "idle";
      // Google SSO will redirect to Google, so we don't update state here
    },
    [googleSsoLogin.rejected]: (state, action) => {
      state.status = "idle";
    },
    [verifyOtp.pending]: (state, action) => {
      state.status = "pending";
    },
    [verifyOtp.fulfilled]: (state, action) => {
      state.status = "idle";
      state.isAuthenticated = true;
      const auth = action.payload;
      // Build user with role claims from signed JWT (tamper-proof)
      const secureUser = buildSecureUser(auth);
      const secureAuth = { ...auth, user: secureUser };
      localStorage.setItem("auth", JSON.stringify(secureAuth));
      state.user = secureUser;
      client.setAuthorizationToken(auth["access_token"]);
      state.pendingChallenge = null;
      state.twoFactorMessage = "";
      state.twoFactorEmailHint = "";
    },
    [verifyOtp.rejected]: (state, action) => {
      state.status = "idle";
      state.isAuthenticated = false;
    },
    [handleGoogleCallback.pending]: (state, action) => {
      state.status = "pending";
    },
    [handleGoogleCallback.fulfilled]: (state, action) => {
      state.status = "idle";
      state.isAuthenticated = true;
      const auth = action.payload;
      // Build user with role claims from signed JWT (tamper-proof)
      const secureUser = buildSecureUser(auth);
      const secureAuth = { ...auth, user: secureUser };
      localStorage.setItem("auth", JSON.stringify(secureAuth));
      state.user = secureUser;
      client.setAuthorizationToken(auth["access_token"]);
    },
    [handleGoogleCallback.rejected]: (state, action) => {
      state.status = "idle";
      state.isAuthenticated = false;
    },
  },
});

export const { logout, clearTwoFactorChallenge } = authenticationSlice.actions;

export default authenticationSlice.reducer;
